import { Box, Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import astrocartographyStyles from "../Astrocartography.styles";
import { FALLBACK_LINE_COLOR } from "../utils/astrocartographyConstants";
import { getPlanetGlyph } from "../utils/astrocartographyUtils";

interface AstroPlanetVisibilityControlProps {
  planets: string[];
  planetColors: Record<string, string>;
  planetVisibility: Record<string, boolean>;
  onPlanetVisibilityChange: (planet: string, value: boolean) => void;
  onAllPlanetsVisible: (value: boolean) => void;
}

/**
 * Planet toggles as chips tinted with each planet's own line colour, so the
 * legend and the on/off control are the same element.
 */
const AstroPlanetVisibilityControl = (
  props: AstroPlanetVisibilityControlProps,
) => {
  const classes = astrocartographyStyles;
  const { t } = useTranslation();
  const {
    planets,
    planetColors,
    planetVisibility,
    onPlanetVisibilityChange,
    onAllPlanetsVisible,
  } = props;

  if (!planets.length) {
    return (
      <Typography variant="caption">{t("astroNoPlanetsAvailable")}</Typography>
    );
  }

  return (
    <Stack spacing={1.25}>
      <Box sx={classes.planetGrid}>
        {planets.map((planet) => {
          const isVisible = planetVisibility[planet] !== false;
          const color = planetColors[planet] ?? FALLBACK_LINE_COLOR;
          return (
            <Tooltip
              key={planet}
              title={isVisible ? t("astroHidePlanet") : t("astroShowPlanet")}
            >
              <Chip
                variant="outlined"
                label={planet}
                aria-pressed={isVisible}
                onClick={() => onPlanetVisibilityChange(planet, !isVisible)}
                icon={
                  <Box sx={classes.planetGlyph(color, isVisible)}>
                    {getPlanetGlyph(planet)}
                  </Box>
                }
                sx={classes.planetChip(color, isVisible)}
              />
            </Tooltip>
          );
        })}
      </Box>

      <Stack direction="row" spacing={1}>
        <Button
          fullWidth
          size="small"
          variant="outlined"
          onClick={() => onAllPlanetsVisible(true)}
        >
          {t("astroAllPlanets")}
        </Button>
        <Button
          fullWidth
          size="small"
          variant="outlined"
          onClick={() => onAllPlanetsVisible(false)}
        >
          {t("astroNoPlanets")}
        </Button>
      </Stack>
    </Stack>
  );
};

export default AstroPlanetVisibilityControl;
